import React from "react";
import { Slider } from "antd";
import * as R from "ramda";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const SliderContainer = styled.div`
  width: 100%;
  padding: 0 7px;

  .ant-slider {
    margin: 0 auto;
    margin-top: 2px;
  }
  &
    .ant-slider:not(.ant-slider-disabled)
    > .ant-slider-step
    > .ant-slider-dot:not([aria-disabled="true"]) {
    border-color: ${({ theme }) => theme["@gray-40"]} !important;
  }
  * .ant-slider-handle:not([aria-disabled="true"]),
  &
    .ant-slider:not(.ant-slider-disabled)
    > .ant-slider-step
    > .ant-slider-dot.ant-slider-dot-active:not([aria-disabled="true"]) {
    border-color: ${({ theme }) => theme["@primary"]} !important;
  }
  * .ant-slider-rail:not([aria-disabled="true"]) {
    background-color: ${({ theme }) => theme["@gray-40"]} !important;
  }
  * .ant-slider-track {
    background-color: ${({ theme }) => theme["@primary"]} !important;
  }
  * .ant-slider-handle {
    transform: none !important;
  }
`;

const LabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  width: 100%;
`;

const Label = styled.div`
  font-size: 11px;
  text-align: center;
`;

function Range({
  options,
  first,
  last,
  onChange,
  tipFormatter,
  disabled,
  value = [],
  ...props
}) {
  const [internalValue, setInternalValue] = React.useState(value);
  return (
    <Container>
      <SliderContainer>
        <Slider
          {...props}
          range
          disabled={disabled}
          value={internalValue}
          tipFormatter={tipFormatter}
          step={null}
          marks={options}
          onChange={setInternalValue}
          onAfterChange={onChange}
        />
      </SliderContainer>
      <LabelsContainer>
        <Label>{first}</Label>
        <Label>{last}</Label>
      </LabelsContainer>
    </Container>
  );
}

const RangeWrapper = ({
  options: __options = [],
  value: __value,
  onChange: __onChange,
  ...props
}) => {
  const _value = React.useMemo(
    () =>
      R.unless(
        Array.isArray,
        R.pipe(
          R.of,
          R.prepend(__value)
        ),
        __value
      ),
    [__value]
  );
  const _options = React.useMemo(
    () =>
      R.map(
        R.when(R.is(String), option => ({ label: option, value: option })),
        __options
      ),
    [__options]
  );
  const first = React.useMemo(() => R.head(_options), [_options]);
  const last = React.useMemo(() => R.last(_options), [_options]);
  const options = React.useMemo(
    () =>
      _options.reduce((acc, { label, value }, index) => {
        const position = (index * 100) / (_options.length - 1);

        return R.assoc(position, { value, text: label, position }, acc);
      }, {}),
    [_options]
  );
  const value = React.useMemo(
    () =>
      R.map(val =>
        R.pipe(
          R.values,
          R.find(R.propEq("value", val)),
          R.prop("position"),
          R.defaultTo(0)
        )(options)
      )(_value),
    [_value, options]
  );

  const onChange = React.useCallback(
    v =>
      __onChange(
        R.map(
          R.pipe(
            R.prop(R.__, options),
            R.prop("value")
          )
        )(v)
      ),
    [__onChange, options]
  );

  const tipFormatter = React.useCallback(
    R.pipe(
      R.prop(R.__, options),
      R.prop("text")
    ),
    []
  );

  return (
    <Range
      {...props}
      {...{
        value,
        options,
        first: R.prop("label", first),
        last: R.prop("label", last),
        onChange,
        tipFormatter
      }}
    />
  );
};

export default RangeWrapper;
